import React, { ChangeEventHandler, useState } from 'react';

import { Box } from '@opendoor/bricks/core';

import { ChevronDown } from '../icons/ChevronDown';
import { InputContainer, InputElement, InputProps } from './Input';

export type SelectElementProps = {
  options: Array<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    label: string;
  }>;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
} & Omit<InputProps, 'onChange'>;

export type SelectInputOptions = Array<{
  label: string;
  value: string | number | { min?: number; max?: number };
}>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getOptWithValue = (options: SelectInputOptions, eventValue: any) => {
  // Edge case where we were populating the select with option = 0, since '' == 0 => true
  if (eventValue === '') {
    return undefined;
  }
  // we do NOT want strict equality here since the value can be a string or a number
  return options?.find(({ value }) => value == eventValue);
};

export const SelectElement: React.FC<SelectElementProps> = ({
  id,
  defaultValue,
  disabled,
  value,
  options = [],
  name,
  onChange,
  error,
  ...inputProps
}) => {
  const optionElements = options.map(({ value: optionValue, label }) => (
    <option key={optionValue} value={optionValue}>
      {label}
    </option>
  ));
  const currentLabel = getOptWithValue(options, value ?? defaultValue)?.label ?? undefined;
  const [currentValue, setCurrentValue] = useState(currentLabel);

  if (currentLabel === undefined) {
    // if we have value selected, add a hidden blank value to set as the current selected value
    // this makes it so that the first visible item is not selected by default.
    optionElements.unshift(<option key="blank" value="" style={{ display: 'none' }} />);
  }

  return (
    <Box position={['relative']}>
      <InputContainer
        error={error}
        suffix={
          <>
            <ChevronDown />
            <select
              id={id}
              name={name}
              disabled={disabled}
              value={value ?? defaultValue}
              onChange={(event) => {
                if (onChange) {
                  onChange(event);
                }
                const eventValue = event.target.value;
                const opt = getOptWithValue(options, eventValue);
                setCurrentValue(opt?.label ?? `${eventValue}`);
              }}
              style={{
                position: 'absolute',
                opacity: 0,
                width: '100%',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                WebkitAppearance: 'none',
              }}
            >
              {optionElements}
            </select>
          </>
        }
      >
        <InputElement
          id={`${id}-input-display`}
          disabled={disabled}
          onChange={() => {}}
          error={error}
          type="text"
          // prevent VoiceOver/screen readers from picking up this dummy display input
          tabIndex={-1}
          aria-disabled="true"
          {...inputProps}
          defaultValue={undefined}
          value={currentValue || undefined}
        />
      </InputContainer>
    </Box>
  );
};
